/**
 * TimingTower — compact official order with gaps, tyres, stops and status.
 * Reads the same snapshot as every other panel; the order is the engine's
 * authoritative order (timing feed, or published classification after results).
 */
import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp, Minus } from "lucide-react";
import { useRaceSession } from "../RaceSessionProvider";
import { formatGapRow, formatIntervalRow, formatLapTime, positionQuality, renderEntryMotion } from "../playback";

const TYRE_COLOURS = { SOFT: "#EF4444", MEDIUM: "#FBBF24", HARD: "#FFFFFF", INTERMEDIATE: "#22C55E", WET: "#3B82F6" };

export function TyreBadge({ compound, age }) {
    const key = String(compound || "").toUpperCase();
    return (
        <span className="inline-flex items-center gap-1.5" title={key ? `${key} · ${age ?? "?"} laps` : "Tyre unknown"}>
            <span className="h-3 w-3 rounded-full ring-1 ring-white/30" style={{ background: TYRE_COLOURS[key] || "#71717A" }} />
            <span className="font-mono text-[10px] uppercase text-zinc-400">
                {key ? key[0] : "?"}
                {age != null ? ` ${age}` : ""}
            </span>
        </span>
    );
}

function ChangeIcon({ value }) {
    const Icon = value > 0 ? ChevronUp : value < 0 ? ChevronDown : Minus;
    const colour = value > 0 ? "text-emerald-400" : value < 0 ? "text-f1-red" : "text-zinc-700";
    return <Icon className={`h-3.5 w-3.5 ${colour}`} aria-label={value > 0 ? `up ${value}` : value < 0 ? `down ${-value}` : "no change"} />;
}

const STATUS_LABEL = { in_pit: "PIT", stopped: "STOP", retired: "OUT", dns: "DNS", dsq: "DSQ", finished: "FIN", not_classified: "NC" };

export default function TimingTower({ className = "" }) {
    const { snapshot, selectedEntryId, compareEntryId, selectEntry, setCompareEntry, mode, motionStore, cursorRef, layoutLengthM } = useRaceSession();
    const entries = snapshot?.entries || [];
    const orderSource = snapshot?.order_source;
    if (!entries.length) {
        return <div className={`py-10 text-sm text-zinc-500 ${className}`}>Timing unavailable.</div>;
    }
    return (
        <div className={`flex min-h-0 flex-col ${className}`} data-testid="timing-tower" data-state-t={snapshot?.clock?.session_seconds != null ? Number(snapshot.clock.session_seconds).toFixed(3) : undefined} data-applied-through={snapshot?.clock?.applied_through_s != null ? Number(snapshot.clock.applied_through_s).toFixed(3) : undefined}>
            <div className="mb-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600">
                <span>{orderSource === "session_result" ? "Classification" : "Race order"}</span>
                <span>{mode === "live" ? "Gap · Int" : "Gap · Int"}</span>
            </div>
            <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
                <AnimatePresence initial={false}>
                    {entries.map((row) => {
                        const active = row.entry_id === selectedEntryId;
                        const compare = row.entry_id === compareEntryId;
                        const status = STATUS_LABEL[row.status];
                        // The map's own motion state at the cursor (row.motion is only refreshed at 30 s checkpoints).
                        const liveMotion = renderEntryMotion(motionStore, row.entry_id, cursorRef.current.cursor, layoutLengthM);
                        // Same vocabulary as the map rings and the inspector diagnostics (playback.positionQuality).
                        const fix = positionQuality(liveMotion) === "unavailable" || liveMotion.status === "stale" ? "no fix" : liveMotion.relocation ? "held" : liveMotion.reacquired ? "reacquired" : liveMotion.status === "held" ? "held" : null;
                        return (
                            <motion.button
                                layout
                                key={row.entry_id}
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (event.shiftKey) setCompareEntry(compare ? null : row.entry_id);
                                    else selectEntry(row.entry_id);
                                }}
                                transition={{ layout: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } }}
                                aria-pressed={active}
                                aria-label={`P${row.position ?? "-"} ${row.acronym} ${row.full_name || ""}${status ? ` ${status}` : ""}`}
                                className={`group relative flex w-full items-center gap-2.5 rounded-[16px] px-2 py-2 text-left transition-all ${active ? "bg-white/[0.06]" : compare ? "bg-sky-300/[0.06]" : "hover:bg-white/[0.03]"}`}
                                data-testid="timing-row"
                                data-entry-id={row.entry_id}
                            >
                                <span className={`absolute left-0 top-2 bottom-2 w-[2px] rounded-full ${active || compare ? "opacity-100" : "opacity-45"}`} style={{ background: row.team_colour || "#E10600" }} />
                                <span className={`w-6 text-center font-mono text-sm ${row.position <= 3 ? "text-white" : "text-zinc-500"}`}>{row.position ?? "–"}</span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-sm font-semibold tracking-[0.12em] text-white">{row.acronym}</span>
                                        <span className="font-mono text-[9px] text-zinc-600">#{row.driver_number}</span>
                                        {status ? <span className={`rounded-full px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.16em] ${row.status === "in_pit" ? "bg-amber-400/15 text-amber-200" : "bg-white/[0.06] text-zinc-400"}`}>{status}</span> : null}
                                        {fix ? <span className={`font-mono text-[8px] uppercase tracking-[0.16em] ${fix === "reacquired" ? "text-rose-300/80" : "text-amber-200/70"}`} title={fix === "no fix" ? "Position signal stale or unavailable" : fix === "held" ? "Position held at the last reliable sample (provider relocation or gap)" : "Position reacquired after a provider relocation"} data-testid="timing-row-fix" data-fix={fix}>{fix}</span> : null}
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-600">
                                        <span>{formatLapTime(row.last_lap?.duration_seconds)}</span>
                                        {row.pit_count ? <span>· {row.pit_count} stop{row.pit_count === 1 ? "" : "s"}</span> : null}
                                        {row.pending_penalty_seconds ? <span className="text-f1-red">· +{row.pending_penalty_seconds}s</span> : null}
                                    </div>
                                </div>
                                <div className="hidden sm:block">
                                    <TyreBadge compound={row.stint?.compound} age={row.tyre_age_laps} />
                                </div>
                                <div className="text-right">
                                    <div className="font-mono text-[11px] tabular-nums text-zinc-200">{formatGapRow(row)}</div>
                                    <div className="font-mono text-[10px] tabular-nums text-zinc-600">{formatIntervalRow(row)}</div>
                                </div>
                                <ChangeIcon value={row.position_change_from_grid || 0} />
                            </motion.button>
                        );
                    })}
                </AnimatePresence>
            </div>
        </div>
    );
}
